export const PHONE_UNAVAILABLE = 'Unavailable';

export const PHONE_NOT_PUBLIC_LABEL = 'No public phone number';

export const isLidOrJid = (value: string | null | undefined): boolean => {
    if (!value || typeof value !== 'string') return false;
    const v = value.trim();
    return v.includes('@lid') || v.includes('@g.us') || v.includes('@s.whatsapp') || v.includes('@');
};

export const isLidOnlyAddress = (value: string | null | undefined): boolean => {
    if (!value || typeof value !== 'string') return false;
    return value.toLowerCase().includes('@lid');
};

export const getDisplayPhone = (phone: string | null | undefined): string => {
    if (!phone) return '';
    if (isLidOrJid(phone)) return PHONE_UNAVAILABLE;
    return formatPhoneNumber(phone);
};

/** Mirror of SparkWebhookService placeholder + system actor names (keep in sync). */
const PLACEHOLDER_CONTACT_NAMES = new Set([
    'chave padrão',
    'chave padrao',
    'default key',
    'default',
    'unknown',
    'unavailable',
    'whatsapp user',
    'usuário',
    'usuario',
    'ia',
    'ai',
    'bot',
    'system',
    'assistant',
    'você',
    'voce',
    'you',
]);

/**
 * Prefer a real person name; never let Spark placeholders / system labels overwrite a good name.
 */
export function resolveContactDisplayName(
    current: string | null | undefined,
    incoming: string | null | undefined,
    number?: string | null,
): string {
    const inc = (incoming || '').trim();
    const cur = (current || '').trim();
    if (inc && !meaninglessContactName(inc, number)) return inc;
    if (cur && !meaninglessContactName(cur, number)) return cur;
    return getContactPickerPrimaryLabel(cur || inc || null, number);
}

function meaninglessContactName(name: string | null | undefined, number: string | null | undefined): boolean {
    const n = (name || '').trim();
    const low = n.toLowerCase();
    if (!n || PLACEHOLDER_CONTACT_NAMES.has(low)) return true;
    const num = (number || '').trim();
    if (!num) return false;
    const numDigits = num.replace(/\D/g, '');
    const nameDigits = n.replace(/\D/g, '');
    if (numDigits.length >= 8 && nameDigits === numDigits) return true;
    if (n === num) return true;
    if (!isLidOrJid(num)) {
        const formatted = formatPhoneNumber(num);
        if (formatted && n === formatted) return true;
    }
    return false;
}

export function getBroadcastTableNameCell(name: string | null | undefined, number: string | null | undefined): string {
    if (!meaninglessContactName(name, number)) return (name || '').trim();
    if (!number?.trim()) return 'Contact';
    if (isLidOrJid(number)) return 'WhatsApp contact';
    return 'Contact';
}

export function getBroadcastTableNumberCell(number: string | null | undefined): string {
    if (!number?.trim()) return '—';
    if (isLidOrJid(number)) return PHONE_NOT_PUBLIC_LABEL;
    return formatPhoneNumber(number);
}

export function getContactPickerPrimaryLabel(name: string | null | undefined, number: string | null | undefined): string {
    const num = number || '';
    if (!meaninglessContactName(name, number)) return (name || '').trim();
    if (!num.trim()) return 'Contact';
    if (!isLidOrJid(num)) return formatPhoneNumber(num) || 'Contact';
    return 'WhatsApp contact';
}

export function getContactPickerSecondaryLabel(name: string | null | undefined, number: string | null | undefined): string {
    const num = number || '';
    if (!num.trim() || isLidOrJid(num)) return '';
    if (meaninglessContactName(name, number)) return '';
    const formatted = formatPhoneNumber(num);
    const primary = getContactPickerPrimaryLabel(name, number);
    if (!formatted || primary === formatted) return '';
    if (primary.replace(/\D/g, '') === formatted.replace(/\D/g, '')) return '';
    return formatted;
}

export const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '';
    const trimmed = phone.trim();
    if (!trimmed) return '';

    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) {
        return hasPlus ? '+' : trimmed;
    }
    if (hasPlus) {
        return `+${digits}`;
    }
    return digits;
};

export const isValidInternationalPhoneLength = (phone: string | null | undefined): boolean => {
    if (!phone || !String(phone).trim()) return false;
    const n = String(phone).replace(/\D/g, '').length;
    return n >= 8 && n <= 15;
};

/** Minimal shape for chat list deduplication (same lead twice: duplicate DB rows or merge bugs). */
export type DedupeableChatContact = {
    id: number;
    number?: string | null;
    isGroup?: boolean;
    updatedAt?: string | Date | null;
    lastMessageTime?: string | null;
};

function contactRecencyScore(c: DedupeableChatContact): number {
    let t = 0;
    if (c.updatedAt) {
        const u = new Date(c.updatedAt as string).getTime();
        if (!Number.isNaN(u)) t = Math.max(t, u);
    }
    if (c.lastMessageTime) {
        const lm = new Date(c.lastMessageTime).getTime();
        if (!Number.isNaN(lm)) t = Math.max(t, lm);
    }
    return Math.max(t, c.id);
}

/**
 * Deduplicates chat sidebar rows: by id first, then by normalized phone (non-groups, 8+ digits),
 * keeping the most recently active row when the same number exists twice.
 */
export function dedupeChatContactsForList<T extends DedupeableChatContact>(contacts: T[]): T[] {
    const byId = new Map<number, T>();
    for (const c of contacts) {
        if (!byId.has(c.id)) byId.set(c.id, c);
    }
    const items = Array.from(byId.values());

    const byDigits = new Map<string, T[]>();
    for (const c of items) {
        if (c.isGroup) continue;
        const digits = (c.number || '').replace(/\D/g, '');
        if (digits.length < 8) continue;
        const arr = byDigits.get(digits) || [];
        arr.push(c);
        byDigits.set(digits, arr);
    }

    const removedIds = new Set<number>();
    for (const arr of Array.from(byDigits.values())) {
        if (arr.length <= 1) continue;
        const best = arr.reduce((a: T, b: T) => (contactRecencyScore(a) >= contactRecencyScore(b) ? a : b));
        for (const c of arr) {
            if (c.id !== best.id) removedIds.add(c.id);
        }
    }

    return items.filter(c => !removedIds.has(c.id));
}

/**
 * Merges the first page from /contacts/list (poll) into the in-memory chat list.
 * Without this, new conversations never appear: the old code only updated rows already in `prev`.
 * Order: API first-page order (recent activity) first, then remaining scrolled pages unchanged.
 *
 * Preserves lastMessage from WebSocket when API poll returns before denormalized last_message is ready
 * (spread `{ ...cur, ...ec }` used to wipe preview with `lastMessage: undefined` from mapped contacts).
 */
function pickNewerContactPreview(
    cur: { lastMessage?: string; lastMessageTime?: string },
    ec: { lastMessage?: string; lastMessageTime?: string }
): { lastMessage?: string; lastMessageTime?: string } {
    const curMsg = (cur.lastMessage ?? '').trim();
    const ecMsg = (ec.lastMessage ?? '').trim();
    const curTime = cur.lastMessageTime ? Date.parse(cur.lastMessageTime) : 0;
    const ecTime = ec.lastMessageTime ? Date.parse(ec.lastMessageTime) : 0;

    if (ecMsg && curMsg) {
        if (!Number.isNaN(ecTime) && !Number.isNaN(curTime) && ecTime >= curTime) {
            return { lastMessage: ec.lastMessage, lastMessageTime: ec.lastMessageTime };
        }
        if (!Number.isNaN(curTime) && (Number.isNaN(ecTime) || curTime > ecTime)) {
            return { lastMessage: cur.lastMessage, lastMessageTime: cur.lastMessageTime };
        }
        return { lastMessage: ec.lastMessage, lastMessageTime: ec.lastMessageTime };
    }
    if (ecMsg) return { lastMessage: ec.lastMessage, lastMessageTime: ec.lastMessageTime };
    if (curMsg) return { lastMessage: cur.lastMessage, lastMessageTime: cur.lastMessageTime };
    return {};
}

export function mergeChatListFirstPageFromPoll<T extends { id: number; isGroup?: boolean; lastMessage?: string; lastMessageTime?: string }>(
    prev: T[],
    firstPageFromApi: T[]
): T[] {
    if (!firstPageFromApi?.length) return prev;
    const byId = new Map<number, T>(prev.map(c => [c.id, c]));
    const firstPageIds: number[] = [];
    for (const ec of firstPageFromApi) {
        if (!ec?.id) continue;
        firstPageIds.push(ec.id);
        const cur = byId.get(ec.id);
        if (cur) {
            const preview = pickNewerContactPreview(cur, ec);
            // O poll (backend) é a fonte da verdade para isGroup: corrige linhas que o realtime
            // inseriu com isGroup errado (o handler de mensagem cravava false até p/ grupos).
            byId.set(ec.id, { ...cur, ...ec, ...preview, isGroup: ec.isGroup ?? cur.isGroup } as T);
        } else {
            byId.set(ec.id, ec);
        }
    }
    const seen = new Set(firstPageIds);
    const fromApiOrder = firstPageIds.map(id => byId.get(id)!).filter(Boolean);
    const rest = prev.filter(c => !seen.has(c.id));
    return [...fromApiOrder, ...rest];
}

type ChatContactRecency = {
    id: number;
    updatedAt?: string | Date | null;
    lastMessageTime?: string | null;
    createdAt?: string | Date | null;
};

/** Timestamp (ms) da atividade mais recente do contato; 0 se desconhecido. */
export function chatContactRecencyMs(c: ChatContactRecency): number {
    let t = 0;
    const consider = (v: unknown) => {
        if (!v) return;
        const ms = new Date(v as string).getTime();
        if (!Number.isNaN(ms)) t = Math.max(t, ms);
    };
    consider(c.lastMessageTime);
    consider(c.updatedAt);
    consider(c.createdAt);
    return t;
}

/**
 * Ordenação ESTÁVEL da lista de conversas: atividade mais recente primeiro, com desempate
 * determinístico por id (desc). Sem o desempate, linhas com o mesmo horário trocam de posição
 * entre renders — uma das causas do "piscar" da lista.
 */
export function compareChatContactsByRecencyDesc(a: ChatContactRecency, b: ChatContactRecency): number {
    const diff = chatContactRecencyMs(b) - chatContactRecencyMs(a);
    if (diff !== 0) return diff;
    return b.id - a.id;
}

type ChatContactSignatureRow = {
    id: number;
    name?: string | null;
    isGroup?: boolean;
    lastMessage?: string;
    lastMessageTime?: string | null;
    aiActive?: number;
    tagId?: number | null;
    archived?: boolean;
    is_viewed?: boolean;
    channel?: string | null;
    sparkChatId?: string | null;
    sparkLinePhone?: string | null;
};

/**
 * Assinatura de CONTEÚDO da lista, independente de ordem. Usada para evitar re-render quando o
 * poll de 5s devolve exatamente os mesmos dados (só uma referência de array nova) — sem isto a
 * lista inteira repinta a cada 5s mesmo sem nenhuma mudança real.
 */
export function chatContactsContentSignature(list: ChatContactSignatureRow[]): string {
    return [...list]
        .sort((a, b) => a.id - b.id)
        .map(c => [
            c.id,
            c.name ?? '',
            c.isGroup ? 1 : 0,
            c.lastMessage ?? '',
            c.lastMessageTime ?? '',
            c.aiActive ?? '',
            c.tagId ?? '',
            c.archived ? 1 : 0,
            c.is_viewed ? 1 : 0,
            c.channel ?? '',
            c.sparkChatId ?? '',
            c.sparkLinePhone ?? '',
        ].join('|'))
        .join('~');
}
